import { DndContext, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { AVATAR_GROUPS } from '@components/design-avatar/avatars';
import { useSortingDefaults } from '@components/drag-and-drop/defaults';
import { EmptyState } from '@components/empty-state';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconDragHover } from '@components/icon-hover-container/icon-hover-container';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconAiAgent } from '@components/icons/ai-agent';
import { IconEdit } from '@components/icons/edit';
import { IconInfoSmall } from '@components/icons/info-small';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { arrayMove } from '@utils/collections';
import { fileNameFromPath } from '@utils/strings';

import { useAIAgentJourneys, useCreateAIAgent, useUpdateAIAgent } from 'api/ai-agents/hooks';
import { AIAgent } from 'api/ai-agents/requests';
import { AttributeFieldType } from 'api/attributes/requests';
import { useUploadFile } from 'api/files/hooks';
import { AIModel, JourneyType } from 'api/types';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';
import { DescriptionFormField } from 'ui/journey-builder/node-types/common';

import { useOutputModal } from './output-modal';

import { JourneyListItem } from '../journeys/journey-item';

export type Property = {
  name: string;
  label: string;
  type: AttributeFieldType;
  instructions: string;
  options?: { value: string }[];
};

type AIAgentFormData = Omit<AIAgent, 'files'> & {
  files: {
    name: string;
    url: string;
  }[];
};

const { t } = getScopedTranslate({
  en: {
    'model4.1HelpText': 'Fast, highly intelligent model with largest context window',
    'model4.1MiniHelpText': 'Balanced for intelligence, speed, and cost',
    'model4.1NanoHelpText': 'Fastest, most cost-effective GPT-4.1 model',
    model4oHelpText: 'Fast, intelligent, flexible GPT model',
    model4oMiniHelpText: 'Fast, affordable small model for focused tasks',
    modelo1HelpText: 'Previous full o-series reasoning model',
    modelo3HelpText: 'The currently most powerful reasoning model',
    modelo3MiniHelpText: 'A small model alternative to o3',
    modelo4MiniHelpText: 'Faster, more affordable reasoning model',
    model5HelpText: 'The best model for coding and agentic tasks across domains',
    model5MiniHelpText: 'A faster, cost-efficient version of GPT-5 for well-defined tasks',
    model5NanoHelpText: 'Fastest, most cost-efficient version of GPT-5',
  },
});

export const aiModelOptions: { value: AIModel; label: AIModel; helpText: string }[] = [
  { value: 'gpt-5', label: 'gpt-5', helpText: t('model5HelpText') },
  { value: 'gpt-5-mini', label: 'gpt-5-mini', helpText: t('model5MiniHelpText') },
  { value: 'gpt-5-nano', label: 'gpt-5-nano', helpText: t('model5NanoHelpText') },
  { value: 'gpt-4.1', label: 'gpt-4.1', helpText: t('model4.1HelpText') },
  { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini', helpText: t('model4.1MiniHelpText') },
  { value: 'gpt-4.1-nano', label: 'gpt-4.1-nano', helpText: t('model4.1NanoHelpText') },
  { value: 'gpt-4o', label: 'gpt-4o', helpText: t('model4oHelpText') },
  { value: 'gpt-4o-mini', label: 'gpt-4o-mini', helpText: t('model4oMiniHelpText') },
  { value: 'o1', label: 'o1', helpText: t('modelo1HelpText') },
  { value: 'o3', label: 'o3', helpText: t('modelo3HelpText') },
  { value: 'o3-mini', label: 'o3-mini', helpText: t('modelo3MiniHelpText') },
  { value: 'o4-mini', label: 'o4-mini', helpText: t('modelo4MiniHelpText') },
];

export const reasoningEffortsOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const modelsUsingResponsesAPI = ['o3', 'o4-mini', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano'];

const DEFAULT_MODEL = 'gpt-5';

const Form = createForm<AIAgentFormData>();

type OutputProps = {
  items?: Array<Property>;
  handleOutputModal?: (index?: number) => void;
  handleDeleteOutput?: (index: number) => void;
  setValue: (value: Array<Property>) => void;
};

export const Output = ({
  items = [],
  handleDeleteOutput,
  handleOutputModal,
  setValue,
}: OutputProps) => {
  const {
    resetState,
    activeId,
    overId,
    offset,
    setOverId,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortingDefaults();

  const itemIds = items.map((item) => item.name);
  const activeItem = items.find((item) => item.name === activeId);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const activeIndex = itemIds.findIndex((id) => id === active.id);
    const overIndex = itemIds.findIndex((id) => id === over?.id);
    if (activeIndex > -1 && overIndex > -1) {
      const newArray = arrayMove(items, activeIndex, overIndex);
      setValue(newArray);
    }
  };

  return (
    <DndContext
      {...sortingDefaults}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragStart={handleDragStart}
    >
      <SortableContext items={itemIds} strategy={strategy}>
        {items.map((item, index) => (
          <OutputItem
            key={item.name}
            item={item}
            index={index}
            handleDeleteOutput={handleDeleteOutput}
            handleOutputModal={handleOutputModal}
          />
        ))}
      </SortableContext>

      <DragOverlay>{activeItem && <OutputItem item={activeItem} index={0} />}</DragOverlay>
    </DndContext>
  );
};

const OutputItem = ({
  item,
  index,
  handleOutputModal,
  handleDeleteOutput,
}: {
  item: Property;
  index: number;
  handleOutputModal?: (index?: number) => void;
  handleDeleteOutput?: (index: number) => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.name,
  });

  return (
    <div ref={setNodeRef}>
      <Card
        spaceX={3}
        spaceY={1}
        interactive
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
          ...(isDragging && {
            opacity: 0,
          }),
        }}
      >
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" space={2} align="center" style={{ overflow: 'hidden' }}>
            <IconDragHover
              icon={<AttributeIcon type={item.type} />}
              {...attributes}
              {...listeners}
            />
            <Text text={item.label} ellipsisWithTooltip />
            <IconInfoSmall color="purple50" tooltip={item.instructions} />
          </Stack>
          <Menu
            buttonProps={{
              variant: 'ghost',
              iconRight: <IconMoreVertical />,
              size: 'sm',
            }}
          >
            <Menu.Item
              label={t('core.edit')}
              icon={<IconEdit />}
              onSelect={() => handleOutputModal?.(index)}
            />
            <Menu.Item
              label={t('core.delete')}
              variant="danger"
              onSelect={() => handleDeleteOutput?.(index)}
              icon={<IconTrash />}
            />
          </Menu>
        </Stack>
      </Card>
    </div>
  );
};

export const useAgentDetailsSidebar = () =>
  useSidebar(AgentDetailsSidebar, { variant: 'CONTENT_HEAVY', closeOnClickOutside: true });

const AgentDetailsSidebar = (props: AIAgent) => {
  const { t } = useScopedTranslate(locale);

  const isUpdate = Boolean(props.shortId);
  const { data: journeys } = useAIAgentJourneys(props.shortId ?? '');

  const tabs = [
    {
      content: <Details {...props} />,
      label: t('details'),
      section: 'details',
    },
    {
      content: <Journeys journeys={journeys ?? []} />,
      label: t('journeys'),
      section: 'journeys',
      tabCount: journeys?.length ?? 0,
    },
  ];

  if (isUpdate) {
    return (
      <Sidebar>
        <Sidebar.SimpleHeader
          title={t('editAgent')}
          icon={IconAiAgent}
          iconColor="blue100"
          tabs={tabs}
        />
      </Sidebar>
    );
  }
  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('createAgent')} icon={IconAiAgent} iconColor="blue100" />
      <Details {...props} />
    </Sidebar>
  );
};

const Journeys = ({ journeys }: { journeys: JourneyType[] }) => {
  const { t } = useScopedTranslate(locale);

  if (!journeys.length) {
    return (
      <Sidebar.Content>
        <EmptyState
          header={t('noJourneys')}
          content={t('noJourneysText')}
          icon={<IconJourneyItem size={40} />}
          iconShadow={<IconJourneyItem size={80} />}
          skipMargin
          variant="small"
        />
      </Sidebar.Content>
    );
  }
  return (
    <Sidebar.Content>
      <Stack space={2}>
        {journeys.map((journey) => (
          <JourneyListItem journey={journey} hideMenu hideActions key={journey.shortId} />
        ))}
      </Stack>
    </Sidebar.Content>
  );
};

const Details = (props: AIAgent) => {
  const { t } = useScopedTranslate(locale);

  const isUpdate = Boolean(props.shortId);
  const createMutation = useCreateAIAgent();
  const updateMutation = useUpdateAIAgent();
  const formStore = useCreateFormStore();
  const showOutputModal = useOutputModal();
  const output = (formStore.useFieldValue('output') as Property[]) ?? props.output;
  const model = formStore.useFieldValue('model') as AIModel;
  const hasStructuredOutput =
    (formStore.useFieldValue('hasStructuredOutput') as boolean) ?? props.hasStructuredOutput;

  const handleOutputModal = (index?: number) => {
    showOutputModal({
      setValue: (value) => formStore.actions.setValue('output', value),
      getValue: () => formStore.getState().values['output'] as Property[],
      currentIndex: index,
    });
  };

  const handleDeleteOutput = (index: number) => {
    const state = formStore.getState();
    const currentOutput = (state.values['output'] ?? []) as Property[];
    formStore.actions.setValue(
      'output',
      currentOutput.filter((_, i) => i !== index)
    );
  };

  const onSubmit = async (formValues: AIAgentFormData) => {
    const mutation = isUpdate ? updateMutation : createMutation;
    const files = formValues.files ?? [];
    const data = {
      ...formValues,
      shortId: props.shortId,
      files: files.map((file) => file.url),
    };
    try {
      await mutation.mutateAsync(data);
      hideSidebar();
    } catch (e) {
      // Handle error?
    }
  };
  const hasOutputError = formStore.useFormErrors().output;

  const uploadMutation = useUploadFile('ai-agent-templates', true);
  return (
    <Form onSubmit={onSubmit} store={formStore}>
      <Sidebar.Content>
        <Stack space={6}>
          <Sidebar.SplitContainer label={t('details')}>
            <Stack space={4}>
              <Form.DesignAvatarChooserField
                id="picture"
                label={t('agentImage')}
                avatars={AVATAR_GROUPS.robots}
                onSelect={uploadMutation.mutateAsync}
                defaultImage={props?.picture}
              />
              <Form.TextField
                id="name"
                label={t('agentName')}
                defaultValue={props?.name}
                validators={['required']}
              />
              <DescriptionFormField defaultValue={props?.description} />
            </Stack>
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          <Sidebar.SplitContainer label={t('configuration')}>
            <Stack space={4}>
              <Form.Select
                id="model"
                label={t('model')}
                defaultValue={props?.model || DEFAULT_MODEL}
                options={aiModelOptions}
                validators={['required']}
                overflowWidth
                isClearable={false}
              />
              <Form.Textarea
                id="systemInstructions"
                placeholder={t('systemInstructionsPlaceholder')}
                label={t('systemInstructions')}
                defaultValue={props?.systemInstructions}
                validators={['required']}
              />
              {model?.startsWith('gpt') && (
                <Form.Slider
                  // @ts-ignore-next-line id is whining -\_(ツ)_/-
                  id="temperature"
                  label={t('temperature')}
                  hideLabel={false}
                  step={0.01}
                  maxValue={2}
                  defaultValue={props?.temperature ?? 1}
                  info={t('temperatureInfo')}
                />
              )}
              {model?.startsWith('o') && (
                <Form.Select
                  id="reasoningEffort"
                  label={t('reasoningEffort')}
                  defaultValue={props?.reasoningEffort ?? 'medium'}
                  options={reasoningEffortsOptions}
                  validators={['required']}
                  isClearable={false}
                />
              )}
            </Stack>
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          <Sidebar.SplitContainer label={t('knowledge')}>
            <Stack space={4}>
              <Text font="regular14" text={t('knowledgeInstructions')} />
              <Form.InstantUploadMultiFileField
                // @ts-ignore-next-line
                id="files"
                label={t('files')}
                hideLabel
                onSelect={uploadMutation.mutateAsync}
                defaultValue={props.files?.map((file) => {
                  return {
                    id: file,
                    name: fileNameFromPath(file),
                    url: file,
                  };
                })}
              />
            </Stack>
          </Sidebar.SplitContainer>
          <Sidebar.Separator />
          {modelsUsingResponsesAPI.includes(model) && (
            <>
              <Sidebar.SplitContainer label={t('tools')}>
                <Stack space={4}>
                  <Text font="regular14" text={t('useWebSearchInstructions')} />
                  <Form.Switch
                    id="useWebSearch"
                    label={t('useWebSearch')}
                    defaultValue={props.useWebSearch ?? false}
                  />
                </Stack>
              </Sidebar.SplitContainer>
              <Sidebar.Separator />
            </>
          )}
          <Sidebar.SplitContainer label={t('output')}>
            <Stack space={4}>
              <Form.Switch
                id="hasStructuredOutput"
                label={t('hasStructuredOutput')}
                defaultValue={isUpdate ? props.hasStructuredOutput : true}
              />
              {hasStructuredOutput && (
                <>
                  <Text font="regular14" text={t('outputInstructions')} />
                  {hasOutputError && <FormError text={t('outputRequired')} />}
                  <Stack space={2}>
                    <Output
                      items={output}
                      handleOutputModal={handleOutputModal}
                      handleDeleteOutput={handleDeleteOutput}
                      setValue={(value) => formStore.actions.setValue('output', value)}
                    />
                    <Stack orientation="horizontal">
                      <Form.HiddenField
                        id="output"
                        validators={hasStructuredOutput ? ['required'] : []}
                        defaultValue={props.output}
                      />
                      <Button
                        variant="secondary"
                        text={t('addOutput')}
                        onClick={() => handleOutputModal()}
                        iconLeft={<IconAddPlus />}
                      />
                    </Stack>
                  </Stack>
                </>
              )}
            </Stack>
          </Sidebar.SplitContainer>
        </Stack>
      </Sidebar.Content>
      <Sidebar.Footer>
        <Form.Actions>
          <Form.Reset text={t('core.cancel')} onClick={hideSidebar} />
          <Form.Submit text={t('core.save')} shouldDisableWhenNotDirty />
        </Form.Actions>
      </Sidebar.Footer>
    </Form>
  );
};

const locale = {
  en: {
    createAgent: 'Create AI Agent',
    editAgent: 'Edit AI Agent',
    agentImage: 'Agent image',
    agentName: 'Agent name',
    details: 'Details',
    systemInstructions: 'System instructions',
    systemInstructionsPlaceholder: 'You are a helpful assistant...',
    configuration: 'Configuration',
    model: 'Model',
    temperature: 'Temperature',
    temperatureInfo:
      'The temperature parameter controls the randomness of the model. Lower values make the model more deterministic, while higher values make it more creative.',
    reasoningEffort: 'Reasoning effort',
    files: 'Files',
    knowledge: 'Knowledge',
    knowledgeInstructions:
      'Configure your agent with information relevant to the context it will be used for',
    output: 'Output',
    hasStructuredOutput: 'Use structured output',
    outputRequired: 'You need to add at least one output',
    outputInstructions: 'Define the data the model should generate',
    addOutput: 'Add',
    journeys: 'Journeys',
    noJourneys: 'No journeys found',
    noJourneysText:
      'No journeys using this AI Agent template are visible to you. It may not be used in any journeys, or you might not have permission to view them',
    tools: 'Tools',
    useWebSearch: 'Use web search',
    useWebSearchInstructions:
      'Enable this to allow your agent to use web search to find the most up-to-date information',
  },
};
